import React, { useState, forwardRef, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Slide,
  Typography,
} from '@mui/material';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import Iconify from '../common/iconify.component';
import Loading from '../table-elements/loading.component';
import SnackbarInfo from '../common/snackbar-info.component';
import { manageRulesetsSequence } from '../../services/ruleset.service';

const Transition = forwardRef((props, ref) => (
  <Slide direction="up" ref={ref} {...props} />
));

const ManageRulesetsOrder = ({
  title,
  onCancel,
  onSuccess,
  scheduleInfoId,
  scheduleInfoRows,
  rulesetsListToDisplay,
}) => {
  const [loading, setLoading] = useState(true);
  const [rowData, setRowData] = useState([]);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [snackbarInfo, setSnackbarInfo] = useState({
    show: false,
    type: '',
    message: '',
  });
  const [isSequenceChanged, setIsSequenceChanged] = useState(false);
  const intialRowData = useRef([]);

  const handleSaveSequence = () => {
    setFormSubmitted(true);

    const payload = {
      newSequence: rowData.map((r) => r.rulesetId),
    };
    manageRulesetsSequence(payload, scheduleInfoId)
      .then(() => {
        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'success',
          message: 'Rulesets sequence updated successfully.',
        });
        setFormSubmitted(false);
        onSuccess();
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setFormSubmitted(false);
      });
  };

  const handleOnDragEnd = (result) => {
    if (result.destination) {
      const newRowData = Array.from(rowData);
      const [draggedItem] = newRowData.splice(result.source.index, 1);
      newRowData.splice(result.destination.index, 0, draggedItem);

      if (
        JSON.stringify(newRowData) === JSON.stringify(intialRowData.current)
      ) {
        setIsSequenceChanged(false);
      } else {
        setIsSequenceChanged(true);
      }
      setRowData(newRowData);
    }
  };

  useEffect(() => {
    intialRowData.current = rulesetsListToDisplay
      .filter(
        (item) =>
          item !== '0' &&
          item !== '-1' &&
          !scheduleInfoRows[item].rulesetName?.includes('Skipped Content')
      )
      .map((rs) => ({
        rulesetId: scheduleInfoRows[rs].rulesetId,
        rulesetName: scheduleInfoRows[rs].rulesetName,
      }));

    setRowData(intialRowData.current);
    setLoading(false);
  }, []);

  return (
    <Dialog
      open
      aria-describedby="rulesets"
      aria-labelledby="rulesets"
      TransitionComponent={Transition}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        {loading && <Loading />}

        {!loading && (
          <DragDropContext onDragEnd={handleOnDragEnd}>
            <Droppable droppableId="rulesets">
              {(provided) => (
                <div {...provided.droppableProps} ref={provided.innerRef}>
                  {rowData.length > 0 &&
                    rowData.map((r, index) => (
                      <Draggable
                        key={`rulesets-${r.rulesetId}`}
                        draggableId={r.rulesetId.toString()}
                        index={index}
                      >
                        {(provide) => (
                          <Card
                            sx={{
                              marginBottom: '5px',
                            }}
                            ref={provide.innerRef}
                            {...provide.draggableProps}
                            {...provide.dragHandleProps}
                          >
                            <CardContent
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                paddingY: '12px',
                                '&:last-child': {
                                  paddingBottom: '12px',
                                },
                              }}
                            >
                              <Typography
                                variant="body2"
                                component="span"
                                color="inherit"
                              >
                                {r.rulesetName}
                              </Typography>
                              <Box
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  textAlign: 'center',
                                }}
                              >
                                <Box
                                  sx={{
                                    display: 'flex',
                                    marginLeft: 20,
                                  }}
                                >
                                  <Iconify icon="ic:round-table-rows" />
                                </Box>
                              </Box>
                            </CardContent>
                          </Card>
                        )}
                      </Draggable>
                    ))}

                  {rowData.length === 0 && (
                    <Paper
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        minHeight: '40vh',
                        alignItems: 'center',
                      }}
                    >
                      No data available.
                    </Paper>
                  )}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        )}
      </DialogContent>

      {!loading && (
        <DialogActions>
          {rowData.length > 0 && (
            <Button
              onClick={handleSaveSequence}
              disabled={
                rowData.length === 0 ||
                rowData.length === 1 ||
                !isSequenceChanged ||
                formSubmitted
              }
            >
              Save
            </Button>
          )}
          <Button onClick={onCancel}>Cancel</Button>
        </DialogActions>
      )}

      {snackbarInfo.show && (
        <SnackbarInfo type={snackbarInfo.type} message={snackbarInfo.message} />
      )}
    </Dialog>
  );
};

ManageRulesetsOrder.propTypes = {
  title: PropTypes.string.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  scheduleInfoId: PropTypes.number.isRequired,
  scheduleInfoRows: PropTypes.object.isRequired,
  rulesetsListToDisplay: PropTypes.array.isRequired,
};

export default ManageRulesetsOrder;
